import {AutocompleteRenderInputParams, Chip, styled, Typography} from '@mui/material';
import {useCallback} from 'react';

import {CarrierLevel} from 'modules/decisionsNext/api/types';

import {carrierLevelLabelMap, carrierLevels} from 'modules/decisionsNext/constants/carrierLevel';

import Autocomplete from 'components/form-next/Autocomplete';
import TextField from 'components/form-next/TextField';

const carrierLevelOptions = carrierLevels.map(level => ({
  id: level,
  label: carrierLevelLabelMap[level],
}));

const SRoot = styled('div')(({theme}) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
}));

const SOptionWrapper = styled('li')(() => ({
  '&&': {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
}));

const SEndAdornmentChip = styled(Chip)(() => ({
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  right: 30,
}));

export const CarrierLevelBlock = ({
  carrierLevel,
  setCarrierLevel,
}: {
  carrierLevel: CarrierLevel;
  setCarrierLevel: (carrierLevel: CarrierLevel) => void;
}) => {
  const renderOption = useCallback(
    (props: React.HTMLAttributes<HTMLLIElement>, option: (typeof carrierLevelOptions)[number]) => {
      return (
        <SOptionWrapper {...props} key={option.id}>
          <Typography variant='body2'>{option.label}</Typography>

          {option.id === CarrierLevel.UltimateParentCarrier && <Chip label='Default' size='small' />}
        </SOptionWrapper>
      );
    },
    []
  );

  const renderInput = useCallback(
    ({InputLabelProps: _, ...params}: AutocompleteRenderInputParams) => {
      const endAdornment =
        carrierLevel === CarrierLevel.UltimateParentCarrier ? (
          <>
            <SEndAdornmentChip label='Default' size='small' />
            {params.InputProps.endAdornment}
          </>
        ) : (
          params.InputProps.endAdornment
        );

      return (
        <TextField
          {...params}
          label='Carrier Level'
          onKeyDown={e => e.stopPropagation()}
          autoComplete='off'
          autoCapitalize='none'
          spellCheck='false'
          color='secondary'
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment,
            },
          }}
        />
      );
    },
    [carrierLevel]
  );

  const selectedOption = carrierLevelOptions.find(option => option.id === carrierLevel);

  return (
    <SRoot>
      <Autocomplete
        options={carrierLevelOptions}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        value={selectedOption}
        onChange={(_, value) => setCarrierLevel(value?.id as CarrierLevel)}
        renderOption={renderOption}
        renderInput={renderInput}
        disableClearable={carrierLevel === CarrierLevel.UltimateParentCarrier}
      />
    </SRoot>
  );
};
